import { useEffect, useState, useMemo } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { SUPPORTED_LANGS } from "@/i18n";

export default function Header({ lang }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [lang, i18n]);

  const basePath = `/${lang}`;

  const links = useMemo(
    () => [
      { to: `${basePath}#top`, label: t("nav.home") },
      { to: `${basePath}#about`, label: t("nav.about") },
      { to: `${basePath}#how`, label: t("nav.how") },
      { to: `${basePath}#projects`, label: t("nav.projects") },
      { to: `${basePath}#contact`, label: t("nav.contact") },
    ],
    [basePath, t]
  );

  const switchLanguage = (nextLang) => {
    if (!SUPPORTED_LANGS.includes(nextLang)) return;
    const pathnameWithoutLang = location.pathname.replace(/^\/(ar|en)(?=\/|$)/, "") || "";
    const nextPath = `/${nextLang}${pathnameWithoutLang}` + (location.hash || "");
    navigate(nextPath, { replace: false });
  };

  const handleAnchor = (e, to) => {
    if (!to.includes("#")) return;
    const [pathPart, hash] = to.split("#");
    if (location.pathname === pathPart || location.pathname === pathPart + "/") {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else if (hash === "top") window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header className="site-header" data-testid="site-header">
      <div className="header-inner">
        <Link to={basePath} className="brand" aria-label="Ronavirexa" data-testid="brand-link">
          <svg className="brand-mark" viewBox="0 0 40 40" aria-hidden="true">
            <rect x="6" y="18" width="6" height="18" fill="currentColor" />
            <rect x="14" y="12" width="6" height="24" fill="currentColor" />
            <rect x="22" y="6" width="6" height="30" fill="currentColor" />
            <rect x="30" y="16" width="4" height="20" fill="currentColor" />
            <circle cx="34" cy="10" r="3" fill="#a63d1e" />
          </svg>
          <span className="brand-name">Ronavirexa</span>
        </Link>

        <nav className="nav-desktop" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={(e) => handleAnchor(e, l.to)}
              className="nav-link"
              data-testid={`nav-${l.label}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="header-actions">
          <div className="lang-switcher" role="group" aria-label={t("lang.switchTo")}>
            <button
              type="button"
              className={`lang-btn ${lang === "ar" ? "lang-btn-active" : ""}`}
              onClick={() => switchLanguage("ar")}
              data-testid="lang-es"
              aria-pressed={lang === "ar"}
            >
              {t("lang.es")}
            </button>
            <span className="lang-sep" aria-hidden="true">/</span>
            <button
              type="button"
              className={`lang-btn ${lang === "en" ? "lang-btn-active" : ""}`}
              onClick={() => switchLanguage("en")}
              data-testid="lang-en"
              aria-pressed={lang === "en"}
            >
              {t("lang.en")}
            </button>
          </div>

          <button
            type="button"
            className="hamburger"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
            aria-expanded={open}
            data-testid="hamburger"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="nav-mobile" data-testid="nav-mobile">
          {links.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={(e) => handleAnchor(e, l.to)}
              className="nav-link-mobile"
            >
              {l.label}
            </a>
          ))}
          <div className="nav-mobile-legal">
            <NavLink to={`${basePath}/privacy`} className="nav-link-mobile-sub">{t("nav.privacy")}</NavLink>
            <NavLink to={`${basePath}/cookies`} className="nav-link-mobile-sub">{t("nav.cookies")}</NavLink>
            <NavLink to={`${basePath}/terms`} className="nav-link-mobile-sub">{t("nav.terms")}</NavLink>
          </div>
        </div>
      )}
    </header>
  );
}
